/*
 * Internal helper: Do NOT import outside of the logger.
 * Purpose: one shared way to send already-sanitised warn/error logs to external services.
 */

export type ExternalLogDestination = {
    /** Whether this vendor is available right now, checked against its own config or global. */
    isLoggingEnabled: () => boolean;
    /** Receives sanitised values: the message first, then any Error and context. Must not throw. */
    forward: (logLevel: number, values: unknown[]) => void;
};

/**
 * Sends the log to every enabled destination; a failing destination never stops the others.
 */
export const forwardLogToDestinations = (
    destinations: ExternalLogDestination[],
    logLevel: number,
    values: unknown[],
): void => {
    destinations.forEach((destination) => {
        try {
            if (destination.isLoggingEnabled()) destination.forward(logLevel, values);
        } catch {
            // Forwarding is best-effort and must never break app flow.
        }
    });
};

/**
 * Splits logger values into the message (first value), the first Error after it, and everything else in order.
 */
export const splitLogValues = (values: unknown[]): { message: string; firstError?: Error; rest: unknown[] } => {
    const message = typeof values[0] === 'string' ? values[0] : '[no-message]';
    let firstError: Error | undefined;
    const rest: unknown[] = [];
    for (let i = 1; i < values.length; i += 1) {
        const value = values[i];
        if (!firstError && value instanceof Error) {
            firstError = value;
            continue;
        }
        rest.push(value);
    }
    return { message, firstError, rest };
};
